import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import Avatar from '@atlaskit/avatar';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import SearchIcon from '@atlaskit/icon/core/search';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import GenericTile from '@atlaskit/tile';
import { SAINFeatureControl } from '@atlassian/search-ai';
import {
	createAtlAttributionHeaderValue,
	type ZeroQueryProduct,
	type ZeroQueryType,
} from '@atlassian/search-client';
import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common';
import { setSessionItem } from '@atlassian/search-dialog';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { getUppercasePrimaryProductKey } from '../../../../common/constants/products';
import { SEARCH_DIALOG_AI_DIALOG_REFERER_KEY } from '../../../../common/constants/quick-find';
import { useAppContext } from '../../../../common/ui/app-context';
import { SearchResult } from '../../../../common/ui/quick-find/search-dialog-result';
import { serveJiraNINFpsNLQNavigation } from '../../../../common/utils/experiments/experiment-exports';
import { INTENT_DETECTION } from '../../../../common/utils/intent-detection';
import { onSuggestedQueryOperational } from '../../../../common/utils/quick-find/events/suggested-query-operational';
import { onSuggestedQuerySelected } from '../../../../common/utils/quick-find/events/suggested-query-selected';
import { onSuggestedQueryShown } from '../../../../common/utils/quick-find/events/suggested-query-shown';
import { type State } from '../../../../common/utils/quick-find/state';
import {
	getZeroQueryVersion,
	is3PZeroQuery,
} from '../../../../common/utils/quick-find/zero-queries';
import { useLogException } from '../../../../controllers/error-boundary/utils';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../controllers/quick-find/analytics';
import {
	mutateZeroQueries,
	mutateZeroQueriesV1,
} from '../../../../controllers/quick-find/fetch-results/zero-queries';
import {
	getActionSubjectId,
	isOpenInNewTabInteraction,
} from '../../../../controllers/quick-find/utils';
import {
	useAnalyticsAttributes,
	useIsHello,
	useSearchSessionId,
} from '../../../../controllers/store/bootstrap';
import {
	useCloudId,
	usePrimaryProduct,
	useUser,
} from '../../../../controllers/store/bootstrap/accessors';
import {
	useQuickFindActions,
	useQuickFindOpen,
	useUpdateRecentQueries,
	useUpdateZeroQueries,
} from '../../../../controllers/store/quick-find';

import { messages } from './messages';
import {
	appIcons,
	removeEntityPlaceholders,
	zeroQueryTemplates,
	// TODO: Uncomment once search quality improves with applied filters and structured queries
	// getZeroQueryParams,
} from './utils';

type HydratedZeroQueryResult = {
	product?: ZeroQueryProduct;
	avatarUrl?: string | null;
	// TODO: Uncomment once search quality improves with applied filters and structured queries
	// accountId?: string | null;
	// dateRange?: ZeroQueryDateRange;
};

const HydratedZeroQueryTitle = React.memo(
	({
		result,
		product,
		avatarUrl,
		zeroQueryType,
	}: {
		avatarUrl?: string | null;
		product?: ZeroQueryProduct;
		result: string;
		zeroQueryType: ZeroQueryType;
	} & HydratedZeroQueryResult) => {
		const intl = useIntl();

		const { fireAnalyticsEvent } = useQuickFindAnalytics();
		const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

		const [operationalEventFired, setOperationalEventFired] = useState(false);

		const ProductIcon = appIcons?.[product!];

		const templateMatch = zeroQueryTemplates.find((template) => result.match(template.pattern));

		if (templateMatch) {
			if (!operationalEventFired) {
				fireAnalyticsEvent(
					onSuggestedQueryOperational({
						success: true,
						attributes: {
							...commonAttributes,
							zeroQueryType,
						},
						nonPrivacySafeAttributes,
					}),
				);
				setOperationalEventFired(true);
			}
		} else {
			if (!operationalEventFired) {
				fireAnalyticsEvent(
					onSuggestedQueryOperational({
						success: false,
						attributes: {
							...commonAttributes,
							zeroQueryType,
							reason: 'Pre-query hydration failed: No matching template',
						},
						nonPrivacySafeAttributes,
					}),
				);
				setOperationalEventFired(true);
			}

			return (
				<Inline alignBlock="center" space="space.050">
					{removeEntityPlaceholders(result)}
				</Inline>
			);
		}

		const name = templateMatch.pattern.exec(result)?.[1];
		const hydratedResult = intl.formatMessage(templateMatch.message, {
			dateIcon: <CalendarIcon label="" />,
			name,
			personAvatar: avatarUrl ? <Avatar label="" size="small" src={avatarUrl} /> : undefined,
			productIcon: ProductIcon ? <ProductIcon /> : undefined,
		});

		return (
			<Inline alignBlock="center" space="space.050">
				{hydratedResult}
			</Inline>
		);
	},
);

type Props = {
	result: string;
	state: State;
	zeroQueryType: ZeroQueryType;
	zeroQueryPosition: number;
} & HydratedZeroQueryResult;

export const ZeroQuery = ({
	result,
	product,
	avatarUrl,
	state,
	zeroQueryType,
	zeroQueryPosition,
	// accountId,
	// dateRange,
}: Props): React.JSX.Element => {
	const intl = useIntl();

	const { generateSearchUrl, queryParams } = useAppContext();

	const [primaryProduct] = usePrimaryProduct();
	const [searchSessionId] = useSearchSessionId();
	const [user] = useUser();
	const [cloudId] = useCloudId();
	const [analyticsAttributes] = useAnalyticsAttributes();
	const updateRecentQueries = useUpdateRecentQueries();
	const updateZeroQueries = useUpdateZeroQueries();
	const [isHello] = useIsHello();

	const { setQuickFindOpen } = useQuickFindActions();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const quickFindOpen = useQuickFindOpen();

	const zeroQueryIs3P = is3PZeroQuery(zeroQueryType);
	const zeroQueryVersion = getZeroQueryVersion(zeroQueryType);

	const intentRouterEnabled =
		serveJiraNINFpsNLQNavigation({
			primaryProduct,
			fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
		})?.cohort === 'test';

	const logException = useLogException();

	const analyticsEventActionSubjectId = getActionSubjectId(state);
	const workspaceId =
		primaryProduct && cloudId && analyticsAttributes?.activationId
			? `ari:cloud:${primaryProduct}:${cloudId}:workspace/${analyticsAttributes.activationId}`
			: '';
	const atlAttributionHeaderValue = fg('kd_zero_query_agg_atl_attribution_header')
		? createAtlAttributionHeaderValue({
				atlWorkspaceId: workspaceId,
				tenantId: cloudId || '',
				product: primaryProduct || '',
			})
		: undefined;

	const onResultClicked = useCallback(
		(event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>) => {
			const newTab = isOpenInNewTabInteraction(event);
			if (!newTab) {
				setQuickFindOpen(false);
			}

			// Set the dialog referrer in sessionStorage
			// This will be used to identify when SearchAIDialog is rendered as a result of this click
			setSessionItem(SEARCH_DIALOG_AI_DIALOG_REFERER_KEY, 'zeroQuery');

			// Move this zero query to the top of the recent list
			if (result && user && user.id && primaryProduct) {
				const zeroQueryResultText = removeEntityPlaceholders(result);
				addRecentSearch(zeroQueryResultText, user.id, primaryProduct, RECENT_SEARCH_KEY);
				updateRecentQueries?.();
			}

			fireAnalyticsEvent(
				onSuggestedQuerySelected({
					actionSubjectId: analyticsEventActionSubjectId,
					attributes: {
						...commonAttributes,
						zeroQueryType,
						zeroQueryPosition,
						...(SAINFeatureControl.isZeroQuerySuggestionsV1Enabled(isHello)
							? {
									zeroQueryVersion,
									is3PZeroQuery: zeroQueryIs3P,
								}
							: {}),
					},
					nonPrivacySafeAttributes,
				}),
			);

			const mutateFn = SAINFeatureControl.isZeroQuerySuggestionsV1Enabled(isHello)
				? mutateZeroQueriesV1
				: mutateZeroQueries;

			mutateFn(
				{
					input: {
						cloudId: cloudId || '',
						query: result,
						product: getUppercasePrimaryProductKey(primaryProduct),
					},
				},
				atlAttributionHeaderValue ? { 'atl-attribution': atlAttributionHeaderValue } : {},
			)
				.then((success) => {
					if (success) {
						updateZeroQueries?.();
					}
				})
				.catch((error) => {
					logException(error);
				});
		},
		[
			commonAttributes,
			fireAnalyticsEvent,
			nonPrivacySafeAttributes,
			primaryProduct,
			result,
			setQuickFindOpen,
			analyticsEventActionSubjectId,
			user,
			cloudId,
			updateRecentQueries,
			updateZeroQueries,
			logException,
			zeroQueryType,
			zeroQueryPosition,
			zeroQueryVersion,
			zeroQueryIs3P,
			atlAttributionHeaderValue,
			isHello,
		],
	);

	const mapZeroQueryToSearchResult = useCallback(
		(query: string) => {
			const strippedQuery = removeEntityPlaceholders(query);

			const icon = fg('qf_icons_recents_suggested') ? (
				<GenericTile label="" size="small">
					<SearchIcon
						label={intl.formatMessage(messages.zero_query_icon_label)}
						color="currentColor"
					/>
				</GenericTile>
			) : (
				<SearchIcon
					label={intl.formatMessage(messages.zero_query_icon_label)}
					color="currentColor"
				/>
			);

			const rightMetadata = <Box>{intl.formatMessage(messages.zero_query)}</Box>;

			const highlightedRightMetadata = (
				<Inline alignBlock="center" space="space.100">
					<Box>{intl.formatMessage(messages.zero_query)}</Box>
					<ReturnIcon />
				</Inline>
			);

			const url = generateSearchUrl({
				...queryParams,
				text: strippedQuery,
				search_id: searchSessionId,
			});

			return {
				id: query,
				icon,
				title: strippedQuery,
				url,
				rightMetadata,
				highlightedRightMetadata,
			};
		},
		[intl, generateSearchUrl, queryParams, searchSessionId],
	);

	const mapHydratedZeroQueryToSearchResult = useCallback(
		({
			result,
			product,
			avatarUrl,
			// TODO: Uncomment once search quality improves with applied filters and structured queries
			// accountId,
			// dateRange,
		}: {
			result: string;
		} & HydratedZeroQueryResult) => {
			const icon = fg('qf_icons_recents_suggested') ? (
				<GenericTile label="" size="small">
					<SearchIcon
						label={intl.formatMessage(messages.zero_query_icon_label)}
						color="currentColor"
					/>
				</GenericTile>
			) : (
				<SearchIcon
					label={intl.formatMessage(messages.zero_query_icon_label)}
					color="currentColor"
				/>
			);

			const rightMetadata = <Box>{intl.formatMessage(messages.zero_query)}</Box>;

			// TODO: Uncomment once search quality improves with applied filters and structured queries
			// const zeroQueryParams = getZeroQueryParams(product, accountId, dateRange);
			const text = removeEntityPlaceholders(result);
			const url = generateSearchUrl({
				// ...zeroQueryParams,
				text,
			});

			const title = (
				<HydratedZeroQueryTitle
					avatarUrl={avatarUrl}
					product={product}
					result={result}
					zeroQueryType={zeroQueryType}
				/>
			);

			return {
				icon,
				title,
				url,
				rightMetadata,
				isThirdParty: zeroQueryIs3P,
			};
		},
		[generateSearchUrl, intl, zeroQueryIs3P, zeroQueryType],
	);

	if (
		SAINFeatureControl.isZeroQuerySuggestionsV1Enabled(isHello) ||
		fg('kd_v0_zero_query_shown_event')
	) {
		// layer and fg checks are stable between renders and won't cause issues with hook calls
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (quickFindOpen) {
				fireAnalyticsEvent(
					onSuggestedQueryShown({
						actionSubjectId: analyticsEventActionSubjectId,
						attributes: {
							...commonAttributes,
							zeroQueryType,
							zeroQueryPosition,
							zeroQueryVersion,
							is3PZeroQuery: zeroQueryIs3P,
							...(intentRouterEnabled
								? { queryClassification: INTENT_DETECTION.value()?.mode }
								: {}),
						},
						nonPrivacySafeAttributes,
					}),
				);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps -- only need to run when quickFindOpen changes
		}, [quickFindOpen]);
	}

	return (
		<SearchResult
			resultType="zeroQueries"
			{...(SAINFeatureControl.isZeroQuerySuggestionsV1Enabled(isHello)
				? mapHydratedZeroQueryToSearchResult({
						result,
						product,
						avatarUrl,
						// TODO: Uncomment once search quality improves with applied filters and structured queries
						// accountId,
						// dateRange
					})
				: mapZeroQueryToSearchResult(result))}
			onResultClicked={onResultClicked}
		/>
	);
};
